import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Table,
    Button,
    Modal,
    Form,
    Input,
    Space,
    message,
    Avatar,
    Select,
    Popconfirm,
    Tag,
    List,
} from "antd";
import {
    PlusOutlined,
    SearchOutlined,
    TeamOutlined,
    UserOutlined,
    SafetyCertificateOutlined,
    DeleteOutlined,
    ReloadOutlined,
} from "@ant-design/icons";
import { api } from "../api";
import { fmtDate } from "../utils";
import ImageUpload from "../components/ImageUpload";

const GroupsPage = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [city, setCity] = useState();
    const [editing, setEditing] = useState(null);
    const [detail, setDetail] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();

    // Group-admins management
    const [adminsGroup, setAdminsGroup] = useState(null);
    const [adminsList, setAdminsList] = useState([]);
    const [adminsLoading, setAdminsLoading] = useState(false);
    const [newAdminEmail, setNewAdminEmail] = useState("");
    const [addingAdmin, setAddingAdmin] = useState(false);

    const loadAdmins = useCallback(async (groupId) => {
        setAdminsLoading(true);
        try {
            const res = await api.listGroupAdmins(groupId);
            setAdminsList(res.admins);
        } catch (e) {
            message.error(e.message);
        } finally {
            setAdminsLoading(false);
        }
    }, []);

    const openAdmins = (g) => {
        setAdminsGroup(g);
        setNewAdminEmail("");
        loadAdmins(g._id);
    };

    const addAdmin = async () => {
        if (!newAdminEmail.trim()) return;
        setAddingAdmin(true);
        try {
            await api.addGroupAdmin(adminsGroup._id, newAdminEmail.trim());
            message.success("Admin added");
            setNewAdminEmail("");
            loadAdmins(adminsGroup._id);
            load();
        } catch (e) {
            message.error(e.message);
        } finally {
            setAddingAdmin(false);
        }
    };

    const removeAdmin = async (userId) => {
        try {
            await api.removeGroupAdmin(adminsGroup._id, userId);
            message.success("Admin removed");
            loadAdmins(adminsGroup._id);
            load();
        } catch (e) {
            message.error(e.message);
        }
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.listGroups({ q: search, city });
            setItems(res.items);
        } catch (e) {
            message.error(e.message);
        } finally {
            setLoading(false);
        }
    }, [search, city]);

    useEffect(() => {
        load();
    }, [load]);

    // City options derived from the loaded groups.
    const cityOptions = useMemo(() => {
        const set = new Set(items.map((g) => g.city).filter(Boolean));
        return [...set].map((c) => ({ value: c, label: c }));
    }, [items]);

    const openEdit = (g) => {
        setEditing(g);
        form.setFieldsValue(g);
    };
    const openCreate = () => {
        setEditing({});
        form.resetFields();
    };

    const save = async () => {
        const values = await form.validateFields();
        setSaving(true);
        try {
            if (editing._id) {
                await api.updateGroup(editing._id, values);
                message.success("Team updated");
            } else {
                await api.createGroup(values);
                message.success("Team created");
            }
            setEditing(null);
            load();
        } catch (e) {
            message.error(e.message);
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        try {
            await api.deleteGroup(id);
            message.success("Team deleted");
            load();
        } catch (e) {
            message.error(e.message);
        }
    };

    const columns = [
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            render: (_, g) => (
                <Space>
                    <Avatar shape="circle" src={g.image} icon={<TeamOutlined />} />
                    <Button type="link" style={{ padding: 0 }} onClick={() => setDetail(g)}>
                        {g.name}
                    </Button>
                </Space>
            ),
        },
        { title: "City", dataIndex: "city", key: "city", render: (c) => c || "—" },
        {
            title: "Events",
            dataIndex: "eventCount",
            key: "eventCount",
            width: 90,
            render: (n) => n || 0,
        },
        {
            title: "Volunteers",
            key: "vol",
            width: 110,
            render: (_, r) => (r.volunteers || []).length,
        },
        {
            title: "Admins",
            key: "admins",
            width: 90,
            render: (_, r) => (r.admins || []).length,
        },
        {
            title: "Actions",
            key: "actions",
            width: 240,
            render: (_, r) => (
                <Space>
                    <Button size="small" onClick={() => openEdit(r)}>
                        Edit
                    </Button>
                    <Button
                        size="small"
                        icon={<SafetyCertificateOutlined />}
                        onClick={() => openAdmins(r)}
                    >
                        Admins
                    </Button>
                    <Popconfirm
                        title="Delete this team?"
                        description="This permanently removes the team."
                        okText="Delete"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => remove(r._id)}
                    >
                        <Button size="small" danger>
                            Delete
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div className="admin-page-head">
                <h1 className="admin-page-title">Teams / Groups</h1>
                <Space>
                    <Button icon={<ReloadOutlined />} loading={loading} onClick={load}>
                        Refresh
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                        New team
                    </Button>
                </Space>
            </div>

            <Space wrap style={{ marginBottom: 16 }}>
                <Input
                    allowClear
                    prefix={<SearchOutlined />}
                    placeholder="Search by name"
                    style={{ width: 240 }}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <Select
                    allowClear
                    placeholder="Filter by city"
                    style={{ width: 200 }}
                    value={city}
                    options={cityOptions}
                    onChange={setCity}
                />
            </Space>

            <Table
                rowKey="_id"
                loading={loading}
                columns={columns}
                dataSource={items}
                scroll={{ x: "max-content" }}
            />

            {/* Detail modal */}
            <Modal
                open={!!detail}
                title={detail ? detail.name : ""}
                onCancel={() => setDetail(null)}
                footer={null}
                width={560}
            >
                {detail && (
                    <>
                        {detail.city && <Tag>{detail.city}</Tag>}
                        <p style={{ color: "#475569", marginTop: 12 }}>
                            {detail.description || "No description."}
                        </p>
                        <p className="admin-cell-sub">
                            Created {fmtDate(detail.createdAt, true)} · {(detail.volunteers || []).length} volunteers ·{" "}
                            {(detail.admins || []).length} admins
                        </p>
                        <h4 style={{ marginTop: 16 }}>
                            Latest posts ({(detail.posts || []).length})
                        </h4>
                        {(detail.posts || []).length === 0 ? (
                            <span className="admin-cell-sub">No posts.</span>
                        ) : (
                            <List
                                size="small"
                                dataSource={(detail.posts || []).slice(-5).reverse()}
                                renderItem={(p) => (
                                    <List.Item>
                                        {typeof p === "object"
                                            ? p.title || p._id || JSON.stringify(p)
                                            : String(p)}
                                    </List.Item>
                                )}
                            />
                        )}
                    </>
                )}
            </Modal>

            {/* Manage admins modal */}
            <Modal
                open={!!adminsGroup}
                title={adminsGroup ? `Admins — ${adminsGroup.name}` : ""}
                onCancel={() => setAdminsGroup(null)}
                footer={null}
                width={520}
            >
                <Space.Compact style={{ width: "100%", marginBottom: 16 }}>
                    <Input
                        placeholder="Add admin by email"
                        value={newAdminEmail}
                        onChange={(e) => setNewAdminEmail(e.target.value)}
                        onPressEnter={addAdmin}
                    />
                    <Button type="primary" loading={addingAdmin} onClick={addAdmin}>
                        Add admin
                    </Button>
                </Space.Compact>
                <List
                    loading={adminsLoading}
                    locale={{ emptyText: "No admins yet" }}
                    dataSource={adminsList}
                    renderItem={(a) => (
                        <List.Item
                            actions={[
                                <Popconfirm
                                    key="rm"
                                    title="Remove this admin?"
                                    okButtonProps={{ danger: true }}
                                    onConfirm={() => removeAdmin(a._id)}
                                >
                                    <Button type="text" danger icon={<DeleteOutlined />} />
                                </Popconfirm>,
                            ]}
                        >
                            <List.Item.Meta
                                avatar={<Avatar src={a.avatar} icon={<UserOutlined />} />}
                                title={a.name || a.email}
                                description={a.email}
                            />
                        </List.Item>
                    )}
                />
            </Modal>

            {/* Create / edit modal */}
            <Modal
                open={!!editing}
                title={editing && editing._id ? "Edit team" : "New team"}
                onCancel={() => setEditing(null)}
                onOk={save}
                confirmLoading={saving}
                destroyOnClose
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label="Name" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="description" label="Description">
                        <Input.TextArea rows={3} />
                    </Form.Item>
                    <Form.Item name="city" label="City">
                        <Input />
                    </Form.Item>
                    <Form.Item name="image" label="Image">
                        <ImageUpload />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default GroupsPage;
